import jwtDecode from "jwt-decode";
import LocalStorage from "../util/storage";
import ServiceRegistry from "./service-registry";
import AppConfig from "../config/app-config";

const AuthenticationService = () => {
	const serviceInstance = ServiceRegistry.createService(
		process.env.REACT_APP_GAMEDAY_API_ENDPOINT
	);

	const storeToken = (profile, token) => {
		const authData = { profile, ...token };
		LocalStorage.set("authData", authData);

		return authData;
	};

	const removeAuthData = () => {
		return LocalStorage.remove("authData");
	};

	const getProfile = (idToken) => {
		const decoded = jwtDecode(idToken);
		return {
			username: decoded.email,
			id: decoded["custom:AccountId"] || "",
			name: decoded["custom:FirstName"] || "",
			lastName: decoded["custom:LastName"] || "",
			su: decoded["custom:SU"] || ""
		};
	};

	const authenticateUsingUrl = () => {
		if (typeof window !== "undefined" && global.env !== "test") {
			const params = new URLSearchParams(window.location.search);
			const idToken = params.get("token");
			const refreshToken = params.get("refreshToken");
			if (idToken && refreshToken) {
				try {
					removeAuthData();
					const profile = getProfile(idToken);
					const authData = { idToken, refreshToken };
					storeToken(profile, authData);
				} catch (e) {
					console.error("The token provided in the URL is invalid");
				}
			}
		}
	};

	const getAuthData = () => {
		authenticateUsingUrl();
		return LocalStorage.get("authData");
	};

	return {
		async authenticate(credentials) {
			try {
				const response = await serviceInstance.post(
					AppConfig.SECURITY.AUTH,
					credentials
				);

				const { idToken } = response.data;
				const profile = getProfile(idToken);

				return storeToken(profile, response.data);
			} catch (e) {
				console.error("The token received from the server is invalid");
				removeAuthData();
				throw e;
			}
		},

		async refresh() {
			const authData = getAuthData() || {};
			const { profile, refreshToken } = authData;
			if (refreshToken) {
				try {
					const response = await serviceInstance.post(AppConfig.SECURITY.REFRESH, {
						refreshToken
					});

					const refreshedToken = response.data;
					if (refreshedToken.refreshToken === null) {
						refreshedToken.refreshToken = refreshToken;
					}

					return storeToken(profile, refreshedToken);
				} catch (e) {
					removeAuthData();
					throw e;
				}
			}

			return null;
		},

		async signOut() {
			return removeAuthData();
		},

		isAuthenticated() {
			return !!getAuthData();
		},

		getProfile() {
			const authData = getAuthData();
			return authData ? authData.profile : null;
		},

		getAuthToken() {
			const authData = getAuthData() || {};
			const { idToken } = authData;

			return idToken;
		}
	};
};

export default AuthenticationService();
